import { Flex, Form, Button, Select } from 'antd';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormSchema } from '../../hooks/use-form-schema';
import type { EditFormValues } from '../../lib/types';
import { buildAssignablePermissionOptions } from '../../lib/permission-options';
import styles from './edit-form.module.scss';
import classNames from 'classnames';
import { CloseSmall } from '@shared/assets/icons/components';
import type { TeamMemberInfo } from '@shared/api';
import { useEffect, type FC } from 'react';
import { Text } from '@shared/ui/antd';
import { User } from '@shared/ui/user';
import { buildAccessRights } from '@shared/constants';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';

interface EditFormProps {
  selectedTeammate?: TeamMemberInfo;
  onClose?: () => void;
  onSubmit: (teamMemberId: number, values: EditFormValues) => Promise<void>;
  // onTransferOwner?: () => void;
  onDelete: () => void;
}

export const EditForm: FC<EditFormProps> = ({
  selectedTeammate,
  onClose,
  onSubmit,
  // onTransferOwner,
  onDelete,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'team' });
  const accessRights = buildAccessRights(t);

  const spaceFormSchema = useFormSchema();
  const currentTeammatePermission = selectedTeammate?.permission;
  const currentUserId = useAppSelector(authSelectors.selectUserInfo)?.id;
  const selectedTeammateIsCurrent = currentUserId === selectedTeammate?.user_info?.id;
  const accessOptions = buildAssignablePermissionOptions(t);

  const {
    handleSubmit,
    control,
    reset,
    clearErrors,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<EditFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(spaceFormSchema),
    defaultValues: {
      permission: undefined,
    },
  });

  const handleSubmitTeamMember = (values: EditFormValues) => {
    if (!selectedTeammate?.user_info?.id) return;

    onSubmit(selectedTeammate.user_info.id, values).then(() => {
      reset();
      onClose?.();
    });
  };

  const handleDelete = () => {
    reset();
    onClose?.();
    onDelete();
  };

  useEffect(() => {
    reset({
      permission: selectedTeammate?.permission,
    });
  }, [selectedTeammate]);

  return (
    <Flex vertical>
      <Flex align="center" justify="space-between" style={{ padding: '24px 12px 12px' }}>
        <Text variant="2xl" fontWeight={600}>
          {t('modal.editTitle')}
        </Text>

        <CloseSmall onClick={onClose} style={{ cursor: 'pointer' }} />
      </Flex>

      <Flex gap="9px" align="center" justify="space-between" style={{ padding: '0 12px 12px' }}>
        <User
          size="sm"
          fullName={selectedTeammate?.user_info?.full_name || '-'}
          caption={selectedTeammate?.user_info?.email || '-'}
        />

        {/* {onTransferOwner && !selectedTeammateIsCurrent && (
          <Button
            className={classNames(styles.button, styles.button_cancel)}
            variant="text"
            onClick={onTransferOwner}
          >
            {t('modal.assignOwner')}
          </Button>
        )} */}
      </Flex>

      <Form
        onChange={() => clearErrors('root')}
        layout="vertical"
        onFinish={handleSubmit(handleSubmitTeamMember)}
      >
        <div className={styles.divider} />

        {currentTeammatePermission === 'OWNER' ? (
          <Flex vertical gap="8px" align="start" className={styles.content}>
            <Text variant="sm" fontWeight={600}>
              {t('modal.permission')}
            </Text>
            <Flex className={styles.permission}>
              <Text variant="sm" fontWeight={500}>
                {accessRights[currentTeammatePermission]?.title}
              </Text>
            </Flex>
          </Flex>
        ) : (
          <Form.Item
            name="permission"
            label={t('modal.permission')}
            help={errors.permission && errors.permission.message}
            validateStatus={errors.permission ? 'error' : ''}
            className={styles.content}
          >
            <Controller
              name="permission"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  style={{ textAlign: 'start', height: '38px' }}
                  options={accessOptions}
                  disabled={selectedTeammateIsCurrent}
                  optionRender={({ label, data }) => (
                    <Flex vertical gap="4px">
                      <Text variant="sm" fontWeight={600}>
                        {label}
                      </Text>
                      <Text variant="xs" fontWeight={600} style={{ color: '#666F83' }}>
                        {data.description}
                      </Text>
                    </Flex>
                  )}
                />
              )}
            />
          </Form.Item>
        )}

        <div className={styles.divider} />

        <Flex gap="16px" align="center" justify="center" className={styles.button_container}>
          <Button
            className={styles.button}
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={!isValid || isSubmitting || !isDirty}
          >
            {t('modal.editConfirm')}
          </Button>

          <Button
            variant="text"
            onClick={handleDelete}
            className={classNames(styles.button, styles.button_cancel)}
            disabled={selectedTeammateIsCurrent}
          >
            {t('modal.removeFromTeam')}
          </Button>
        </Flex>
      </Form>
    </Flex>
  );
};
